/**
 * CommonWeaknessCard
 *
 * Displays the most-common body-part weakness labels for a given
 * (channelSlug, templateId) pair.  Consumes the Phase E.2 endpoint via
 * useCommonWeaknesses.
 *
 * Three visual states:
 * 1. Loading — skeleton bars
 * 2. Empty (no scored evals) — explanatory hint about legacy data
 * 3. Empty (scored but no patterns) — "varied issues" message
 * 4. Populated — amber-tinted bar chart of top N labels
 */

import { useCommonWeaknesses } from '@/hooks/useCommonWeaknesses';

// ─── Props ─────────────────────────────────────────────────────────────────────

interface CommonWeaknessCardProps {
  channelSlug: string;
  templateId: string;
  /** Number of weakness rows to request (1-25). Defaults to 5. */
  limit?: number;
}

// ─── Skeleton bar ─────────────────────────────────────────────────────────────

function SkeletonBar({ width }: { width: string }) {
  return (
    <div
      className="animate-pulse rounded"
      style={{
        height: '36px',
        width,
        background: 'var(--color-border-subtle)',
      }}
    />
  );
}

// ─── Weakness row ─────────────────────────────────────────────────────────────

interface WeaknessBarRowProps {
  label: string;
  frequency: number;
  athletesAffected: number;
  maxFrequency: number;
}

function WeaknessBarRow({
  label,
  frequency,
  athletesAffected,
  maxFrequency,
}: WeaknessBarRowProps) {
  const pct = maxFrequency > 0 ? (frequency / maxFrequency) * 100 : 0;
  const athleteWord = athletesAffected === 1 ? 'athlete' : 'athletes';

  return (
    <div style={{ marginBottom: '10px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '4px',
        }}
      >
        <span
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: '11px',
            color: 'var(--color-text-muted)',
            whiteSpace: 'nowrap',
            marginLeft: '8px',
          }}
        >
          {frequency} occurrence{frequency !== 1 ? 's' : ''} · {athletesAffected} {athleteWord}
        </span>
      </div>
      {/* Bar track */}
      <div
        style={{
          height: '6px',
          borderRadius: '3px',
          background: 'var(--color-border-subtle)',
          overflow: 'hidden',
        }}
      >
        {/* Fill — amber-tinted to signal a weak area */}
        <div
          data-testid={`weakness-bar-${label}`}
          style={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: '3px',
            background: 'var(--color-accent-gold, #F59E0B)',
            transition: 'width 0.4s ease',
          }}
        />
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CommonWeaknessCard({
  channelSlug,
  templateId,
  limit,
}: CommonWeaknessCardProps) {
  const { data, isLoading, isError, error } = useCommonWeaknesses(
    channelSlug,
    templateId,
    limit,
  );

  // ── Loading ──
  if (isLoading) {
    return (
      <div
        style={{
          background: 'var(--color-dark-card)',
          borderRadius: '12px',
          border: '1px solid var(--color-border-subtle)',
          padding: '20px',
        }}
      >
        <div
          className="animate-pulse rounded"
          style={{
            height: '16px',
            width: '160px',
            background: 'var(--color-border-subtle)',
            marginBottom: '16px',
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <SkeletonBar width="90%" />
          <SkeletonBar width="70%" />
          <SkeletonBar width="55%" />
          <SkeletonBar width="40%" />
          <SkeletonBar width="28%" />
        </div>
      </div>
    );
  }

  // ── Error ──
  if (isError) {
    const msg =
      error instanceof Error ? error.message : 'Failed to load weakness data.';
    return (
      <div
        style={{
          background: 'var(--color-dark-card)',
          borderRadius: '12px',
          border: '1px solid var(--color-border-subtle)',
          padding: '20px',
          color: 'var(--color-accent-red)',
          fontSize: '13px',
        }}
      >
        {msg}
      </div>
    );
  }

  // ── Empty — no scored evals yet (legacy data) ──
  if (!data || data.total_evals_scored === 0) {
    return (
      <div
        style={{
          background: 'var(--color-dark-card)',
          borderRadius: '12px',
          border: '1px solid var(--color-border-subtle)',
          padding: '20px',
        }}
      >
        <p
          style={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            marginBottom: '8px',
          }}
        >
          Common Weaknesses
        </p>
        <p
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            lineHeight: 1.6,
          }}
        >
          Common weakness analysis requires evaluations from this template's
          recent activity. Older evals don't have observation data yet.
        </p>
      </div>
    );
  }

  // ── Empty — scored evals exist but no patterns detected ──
  if (data.weaknesses.length === 0) {
    return (
      <div
        style={{
          background: 'var(--color-dark-card)',
          borderRadius: '12px',
          border: '1px solid var(--color-border-subtle)',
          padding: '20px',
        }}
      >
        <p
          style={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            marginBottom: '8px',
          }}
        >
          Common Weaknesses
        </p>
        <p
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            lineHeight: 1.6,
          }}
        >
          No common weakness patterns detected — athletes are showing varied
          issues.
        </p>
      </div>
    );
  }

  // ── Populated ──
  const maxFrequency = Math.max(...data.weaknesses.map((w) => w.frequency));

  return (
    <div
      style={{
        background: 'var(--color-dark-card)',
        borderRadius: '12px',
        border: '1px solid var(--color-border-subtle)',
        padding: '20px',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '16px' }}>
        <p
          style={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            marginBottom: '2px',
          }}
        >
          Common Weaknesses
        </p>
        <p
          style={{
            fontSize: '11px',
            color: 'var(--color-text-muted)',
          }}
        >
          Across {data.total_evals_scored} of {data.total_evals_in_scope} recent evals
        </p>
      </div>

      {/* Weakness bars */}
      {data.weaknesses.map((w) => (
        <WeaknessBarRow
          key={w.observation_label}
          label={w.observation_label}
          frequency={w.frequency}
          athletesAffected={w.athletes_affected}
          maxFrequency={maxFrequency}
        />
      ))}
    </div>
  );
}
